/**
 * Help-desk admin — gestione ticket reali (entità Ticket / JSON:API `tickets`).
 *
 * Funzionalità:
 *  - mantine-react-table con colonne: numero, oggetto (Message troncato), status,
 *    severity (Priority), assegnato a, creato il, autore (FirstName/LastName).
 *  - Filtri per status e severity, persistiti nei search params dell'URL
 *    (validateSearch sulla route admin.help-desk.tsx; qui arrivano via props).
 *  - Click su riga apre un drawer con dettaglio, risposta, assegnazione a utente e
 *    cambio status.
 *  - Bulk action: chiudi i ticket selezionati (status = "closed").
 */
import {
  ActionIcon, Badge, Box, Button, Card, Drawer, Group, Loader, Select, Stack, Text,
  Textarea, TextInput, Title, Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconClipboardList, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'mantine-react-table';
import dayjs from 'dayjs';
import { fetchList, updateOne } from '@/shared/api/jsonapi/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { useJsonApiTable } from '@/shared/components/DataTable/useJsonApiTable';

/** Wire-format del Ticket lato JSON:API (camelCase). */
interface TicketRow extends Denormalized {
  type: 'tickets';
  number?: number;
  email?: string;
  organization?: string;
  vat?: string;
  status?: string;
  message?: string;
  creationDate?: string;
  phone?: string;
  priority?: number;
  closedById?: string;
  note?: string;
  closedDate?: string | null;
  answer?: string;
  firstName?: string;
  lastName?: string;
  organizationToBeConfirmed?: string;
  /** Relazione `assigned` denormalizzata dal parser (può essere identifier o utente completo). */
  assigned?: { type: string; id: string; firstName?: string; lastName?: string; email?: string } | null;
}

interface UserOption extends Denormalized {
  type: 'users';
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const STATUS_OPTIONS = [
  { value: 'open', label: 'Aperto' },
  { value: 'in-progress', label: 'In lavorazione' },
  { value: 'closed', label: 'Chiuso' },
];

export const SEVERITY_OPTIONS = [
  { value: '1', label: 'Bassa' },
  { value: '2', label: 'Media' },
  { value: '3', label: 'Alta' },
  { value: '4', label: 'Urgente' },
];

function statusColor(s?: string): string {
  if (!s) return 'gray';
  const v = s.toLowerCase();
  if (v === 'open') return 'blue';
  if (v === 'in-progress' || v === 'in_progress') return 'orange';
  if (v === 'closed' || v === 'resolved') return 'green';
  return 'gray';
}

function severityColor(p?: number): string {
  if (p == null) return 'gray';
  if (p >= 4) return 'red';
  if (p === 3) return 'orange';
  if (p === 2) return 'blue';
  return 'gray';
}

function severityLabel(p?: number): string {
  if (p == null) return '—';
  return SEVERITY_OPTIONS.find((o) => o.value === String(p))?.label ?? String(p);
}

function authorOf(t: TicketRow): string {
  const name = `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim();
  if (name) return name;
  return t.email ?? '—';
}

/** Filtri rapidi persistiti nei search params dell'URL (assente = nessun filtro). */
export interface HelpDeskSearch {
  /** 'open' | 'in-progress' | 'closed'. */
  status?: string;
  /** Priority come stringa '1'..'4'. */
  severity?: string;
}

interface HelpDeskAdminProps {
  search: HelpDeskSearch;
  /** Merge del patch nei search params correnti (replace history). */
  onSearchChange: (patch: Partial<HelpDeskSearch>) => void;
}

export function HelpDeskAdmin({ search, onSearchChange }: HelpDeskAdminProps) {
  const qc = useQueryClient();
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'creationDate', desc: true }]);
  const [filters, setFilters] = useState<MRT_ColumnFiltersState>([]);
  const statusFilter = search.status ?? null;
  const severityFilter = search.severity ?? null;
  const [selectedRow, setSelectedRow] = useState<TicketRow | null>(null);
  const [drawerOpened, drawer] = useDisclosure(false);

  // Combina i filtri "rapidi" con i column filters MRT.
  const mergedFilters = useMemo<MRT_ColumnFiltersState>(() => {
    const base = [...filters];
    if (statusFilter) base.push({ id: 'status', value: statusFilter });
    if (severityFilter) base.push({ id: 'priority', value: Number(severityFilter) });
    return base;
  }, [filters, statusFilter, severityFilter]);

  const { data, isLoading, isError } = useJsonApiTable<TicketRow>('tickets', {
    pagination,
    sorting,
    filters: mergedFilters,
    include: ['assigned'],
  });

  const patch = useMutation({
    mutationFn: ({ id, attrs, assignedUserId }: { id: string; attrs?: Partial<TicketRow>; assignedUserId?: string | null }) => {
      const body: { id: string; attributes?: Record<string, unknown>; relationships?: Record<string, { data: { type: string; id: string } | null }> } = { id };
      if (attrs && Object.keys(attrs).length > 0) {
        body.attributes = attrs as Record<string, unknown>;
      }
      if (assignedUserId !== undefined) {
        body.relationships = {
          assigned: assignedUserId ? { data: { type: 'users', id: assignedUserId } } : { data: null },
        };
      }
      return updateOne('tickets', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jsonapi', 'tickets'] });
      notifications.show({ color: 'green', message: 'Ticket aggiornato' });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore aggiornamento' }),
  });

  const columns = useMemo<MRT_ColumnDef<TicketRow>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'Numero',
        size: 90,
        Cell: ({ cell }) => <Text fw={600}>#{cell.getValue<number | undefined>() ?? '—'}</Text>,
      },
      {
        accessorKey: 'message',
        header: 'Oggetto',
        Cell: ({ cell }) => {
          const v = cell.getValue<string | undefined>() ?? '';
          const trimmed = v.length > 80 ? `${v.slice(0, 80)}…` : v;
          return <Text size="sm">{trimmed || '—'}</Text>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | undefined>();
          return <Badge color={statusColor(v)}>{v ?? 'open'}</Badge>;
        },
      },
      {
        accessorKey: 'priority',
        header: 'Severity',
        size: 110,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | undefined>();
          return <Badge color={severityColor(v)}>{severityLabel(v)}</Badge>;
        },
      },
      {
        id: 'assigned',
        header: 'Assegnato a',
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const a = row.original.assigned;
          if (!a) return <Text size="xs" c="dimmed">—</Text>;
          const label = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.email || a.id.slice(0, 8);
          return <Text size="sm">{label}</Text>;
        },
      },
      {
        accessorKey: 'creationDate',
        header: 'Creato il',
        size: 150,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | undefined>();
          return v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—';
        },
      },
      {
        accessorKey: 'firstName',
        header: 'Autore',
        size: 180,
        enableSorting: false,
        Cell: ({ row }) => <Text size="sm">{authorOf(row.original)}</Text>,
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: data?.data ?? [],
    rowCount: data?.total ?? 0,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isLoading,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    mantineToolbarAlertBannerProps: isError ? { color: 'red', children: 'Errore caricamento' } : undefined,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        setSelectedRow(row.original);
        drawer.open();
      },
      style: { cursor: 'pointer' },
    }),
    initialState: { showColumnFilters: false, density: 'xs' },
    paginationDisplayMode: 'pages',
    enableStickyHeader: true,
  });

  const selectedIds = useMemo(() => Object.keys(table.getState().rowSelection).filter((k) => table.getState().rowSelection[k]), [table]);

  const bulkClose = () => {
    if (selectedIds.length === 0) return;
    modals.openConfirmModal({
      title: `Chiudi ${selectedIds.length} ticket`,
      children: <Text size="sm">I ticket selezionati verranno marcati come chiusi.</Text>,
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: () => {
        selectedIds.forEach((id) => patch.mutate({ id, attrs: { status: 'closed', closedDate: new Date().toISOString() } }));
        table.resetRowSelection();
      },
    });
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>
          <IconClipboardList size={26} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Help Desk — Admin
        </Title>
        <Group>
          <Select
            placeholder="Status"
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => { onSearchChange({ status: v ?? undefined }); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
            clearable
            w={170}
          />
          <Select
            placeholder="Severity"
            data={SEVERITY_OPTIONS}
            value={severityFilter}
            onChange={(v) => { onSearchChange({ severity: v ?? undefined }); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
            clearable
            w={150}
          />
          <Tooltip label="Aggiorna">
            <ActionIcon variant="light" onClick={() => qc.invalidateQueries({ queryKey: ['jsonapi', 'tickets'] })}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button size="xs" color="gray" disabled={selectedIds.length === 0} onClick={bulkClose}>
            Chiudi selezionati ({selectedIds.length})
          </Button>
        </Group>
      </Group>

      {isLoading && <Loader />}

      <Card withBorder padding={0} radius="md">
        <MantineReactTable table={table} />
      </Card>

      <TicketDrawer
        ticket={selectedRow}
        opened={drawerOpened}
        onClose={drawer.close}
        onSave={(args) => {
          if (!selectedRow) return;
          patch.mutate({ id: selectedRow.id, attrs: args.attrs, assignedUserId: args.assignedUserId });
        }}
      />
    </Stack>
  );
}

function TicketDrawer({
  ticket, opened, onClose, onSave,
}: {
  ticket: TicketRow | null;
  opened: boolean;
  onClose: () => void;
  onSave: (args: { attrs?: Partial<TicketRow>; assignedUserId?: string | null }) => void;
}) {
  const [answer, setAnswer] = useState('');
  const [assignedId, setAssignedId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const usersQ = useQuery({
    queryKey: ['users-for-tickets-drawer'],
    queryFn: () => fetchList<UserOption>('users', { page: { size: 50 } }),
    enabled: opened,
  });

  // Reset stato al cambio ticket.
  useEffect(() => {
    setAnswer(ticket?.answer ?? '');
    setAssignedId(ticket?.assigned?.id ?? null);
    setStatus(ticket?.status ?? null);
  }, [ticket?.id, ticket?.answer, ticket?.assigned?.id, ticket?.status]);

  if (!ticket) return null;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={<Text fw={600}>Ticket #{ticket.number ?? '—'}</Text>}
    >
      <Stack>
        <Box>
          <Text size="xs" c="dimmed">Autore</Text>
          <Text size="sm">{authorOf(ticket)}</Text>
        </Box>
        {ticket.organization || ticket.organizationToBeConfirmed ? (
          <Box>
            <Text size="xs" c="dimmed">Organizzazione</Text>
            <Text size="sm">{ticket.organization || ticket.organizationToBeConfirmed}</Text>
          </Box>
        ) : null}
        <Box>
          <Text size="xs" c="dimmed">Messaggio</Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{ticket.message ?? '—'}</Text>
        </Box>

        <Group grow>
          <Select label="Status" data={STATUS_OPTIONS} value={status} onChange={setStatus} clearable={false} />
          <Select
            label="Assegna a"
            placeholder="Seleziona utente"
            searchable
            data={(usersQ.data?.data ?? []).map((u) => ({
              value: u.id,
              label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
            }))}
            value={assignedId}
            onChange={setAssignedId}
            nothingFoundMessage="Nessun utente"
          />
        </Group>

        <Textarea
          label="Risposta"
          minRows={4}
          autosize
          value={answer}
          onChange={(e) => setAnswer(e.currentTarget.value)}
          placeholder="Scrivi la risposta al richiedente…"
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Chiudi</Button>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={() => {
              const attrs: Partial<TicketRow> = {};
              if (status && status !== ticket.status) attrs.status = status;
              if (answer !== (ticket.answer ?? '')) attrs.answer = answer;
              const currentAssigned = ticket.assigned?.id ?? null;
              const assignedChanged = assignedId !== currentAssigned;
              if (Object.keys(attrs).length === 0 && !assignedChanged) {
                onClose();
                return;
              }
              onSave({
                attrs: Object.keys(attrs).length ? attrs : undefined,
                assignedUserId: assignedChanged ? assignedId : undefined,
              });
              onClose();
            }}
          >
            Salva
          </Button>
        </Group>

        <TextInput value={ticket.email ?? ''} label="Email autore" readOnly />
      </Stack>
    </Drawer>
  );
}
